import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import ProfileCard from './ProfileCard';
import '../styles/RelationshipGraph.css';
const Legend = ({ dataType }) => {
  return (
    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-lg">
      <h3 className="text-sm font-semibold mb-3">Legend</h3>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-[#c92833]"></div>
          <span className="text-sm">Selected {dataType === 'hashtag' ? 'Hashtag' : 'Profile'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-[#65ccb7]"></div>
          <span className="text-sm">Common Connection</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-[#707070]"></div>
          <span className="text-sm">Unique Connection</span>
        </div>
      </div>
    </div>
  );
};

const RelationshipGraph = ({ data, layout, relationType, maxNodes, platform }) => {
  const svgRef = useRef();
  const [selectedNode, setSelectedNode] = useState(null);
  const dataType = data[0]?.type || 'profile';
  const [selectedHashtag, setSelectedHashtag] = useState(null);


  
  useEffect(() => {
    if (!data || !data.length || !svgRef.current) return;

    const width = 800;
    const height = 600;
    const svg = d3.select(svgRef.current);

    svg.selectAll("*").remove();

    const g = svg.append("g");

    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoom);

    const nodes = [];
    const links = [];
    const childNodeCounts = new Map();

    const isHashtagData = dataType === 'hashtag';

    if (isHashtagData) {
      // First pass: Count the total profiles across all hashtags
      let totalProfiles = 0;
      data.forEach((hashtag) => {
        const allProfiles = hashtag.profiles || [];
        totalProfiles += allProfiles.length; // Count total profiles
      });

      // Second pass: Create nodes and links for profiles
      data.forEach((hashtag) => {
        // Skip hashtags without an id
        if (!hashtag.id) return;

        // Add hashtag as a parent node
        nodes.push({
          id: hashtag.id,
          type: "parent",
          label: hashtag.label,
          isHashtag: true,
        });

        const allProfiles = hashtag.profiles || [];

        // Create child nodes (profiles) and direct mapping
        allProfiles.slice(0, maxNodes).forEach((profile) => {
          // Skip profiles without a valid username or full_name
          const profileId = profile.username || profile.full_name;
          if (!profileId) return;
        
          // Check if profile is "common" based on total profile count
          const isCommon = totalProfiles > 1; // Since all profiles count for common
        
          const childNode = {
            ...profile,
            id: profileId, // Use username or full_name as unique ID for profiles
            type: "child",
            parentId: hashtag.id,
            isCommon,
            platform,
          };
        
          // Add profile node if not already added
          if (!nodes.some((n) => n.id === profileId)) {
            nodes.push(childNode);
          }
        
          // Add link from hashtag to profile
          links.push({
            source: hashtag.id,
            target: profileId, // Direct mapping without filtering
            type: "child",
            isCommon,
          });
        });
        
      });

    } else {
      const commonFollowers = new Map(); // Track common followers between profiles
    
      data.forEach(profile => {
        if (profile.followers) {
          profile.followers.slice(0, maxNodes).forEach(connection => {
            const uniqueId = connection.username || connection.id; // Fallback to ID if username is null
            if (!uniqueId) return; // Skip if both username and id are null
    
            // Count followers for commonality check
            const count = childNodeCounts.get(uniqueId) || 0;
            childNodeCounts.set(uniqueId, count + 1);
          });
        }
      });
    
      // Now handle the nodes and links
      data.forEach(profile => {
        const uniqueProfileId = profile.username || profile.id; // Ensure unique ID for profile
        if (!uniqueProfileId) return; // Skip if both username and id are null
    
        nodes.push({ ...profile, type: 'parent', id: uniqueProfileId, platform });
    
        if (profile.followers) {
          profile.followers.slice(0, maxNodes).forEach(connection => {
            const uniqueId = connection.username || connection.id;
            if (!uniqueId) return;
    
            // Check if the follower is common between multiple profiles
            const isCommon = childNodeCounts.get(uniqueId) > 1;
    
            // If the follower is common, it should be treated as a single node with color #65ccb7
            if (isCommon) {
              if (!commonFollowers.has(uniqueId)) {
                const commonFollowerNode = {
                  id: uniqueId,
                  type: 'child',
                  isCommon: true,
                  color: '#65ccb7',
                  platform
                };
    
                // Add common follower node only once
                nodes.push(commonFollowerNode);
              }
    
              // Link the common follower to the parent profile
              links.push({
                source: uniqueProfileId,
                target: uniqueId,
                type: 'child',
                isCommon: true
              });
            } else {
              const childNode = {
                ...connection,
                type: 'child',
                id: uniqueId,
                parentId: uniqueProfileId,
                isCommon: false,
                platform
              };
    
              // Add non-common child node
              if (!nodes.some(n => n.id === uniqueId)) {
                nodes.push(childNode);
              }
    
              // Link the non-common follower to its specific parent
              links.push({
                source: uniqueProfileId,
                target: uniqueId,
                type: 'child',
                isCommon: false
              });
            }
          });
        }
      });

    
    
    }

    const uniqueNodes = Array.from(
      new Map(nodes.map(node => [node.id, node])).values()
    );

    const defs = g.append("defs");
    
    // Updated arrow markers with consistent size
    const arrowSize = 8;
    const arrowRefX = 23;
    
    defs.append("marker")
      .attr("id", "arrow")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", arrowRefX)
      .attr("refY", 0)
      .attr("markerWidth", arrowSize)
      .attr("markerHeight", arrowSize)
      .attr("orient", "auto")
      .append("path")
      .attr("fill", "#707070")
      .attr("d", "M0,-5L10,0L0,5");

    defs.append("marker")
      .attr("id", "arrow-common")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", arrowRefX)
      .attr("refY", 0)
      .attr("markerWidth", arrowSize)
      .attr("markerHeight", arrowSize)
      .attr("orient", "auto")
      .append("path")
      .attr("fill", "#65ccb7")
      .attr("d", "M0,-5L10,0L0,5");

    const createDefaultSimulation = () => {
      return d3.forceSimulation(uniqueNodes)
        .force("link", d3.forceLink(links)
          .id(d => d.id)
          .distance(150)
          .strength(1))
        .force("charge", d3.forceManyBody().strength(-800))
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collide", d3.forceCollide().radius(45));
    };

    let simulation;

    if (layout === 'tree') {
      const treeLayout = d3.tree()
        .size([width - 200, height - 100])
        .nodeSize([80, 120]);

      const stratify = d3.stratify()
        .id(d => d.id)
        .parentId(d => d.parentId);

      try {
        const root = stratify(uniqueNodes);
        const treeData = treeLayout(root);

        treeData.descendants().forEach((node, i) => {
          const found = uniqueNodes.find(n => n.id === node.id);
          if (found) {
            found.x = node.x + width / 2;
            found.y = node.y + 50;
            found.depth = node.depth;
          }
        });

        simulation = d3.forceSimulation(uniqueNodes)
          .force("x", d3.forceX(d => d.x).strength(1))
          .force("y", d3.forceY(d => d.y).strength(1))
          .force("collide", d3.forceCollide().radius(45))
          .alphaDecay(0.1);

      } catch (e) {
        console.warn('Tree layout fallback to force layout', e);
        simulation = createDefaultSimulation();
      }
    } else if (layout === 'circular') {
      const radius = Math.min(width, height) / 3;
      
      const parentNodes = uniqueNodes.filter(n => n.type === 'parent');
      const childNodes = uniqueNodes.filter(n => n.type === 'child');
      
      const parentAngleStep = (2 * Math.PI) / parentNodes.length;
      parentNodes.forEach((node, i) => {
        const angle = i * parentAngleStep;
        node.x = width/2 + (radius * 0.5) * Math.cos(angle);
        node.y = height/2 + (radius * 0.5) * Math.sin(angle);
      });
      
      const childAngleStep = (2 * Math.PI) / childNodes.length;
      childNodes.forEach((node, i) => {
        const angle = i * childAngleStep;
        node.x = width/2 + radius * Math.cos(angle);
        node.y = height/2 + radius * Math.sin(angle);
      });

      simulation = d3.forceSimulation(uniqueNodes)
        .force("x", d3.forceX(d => d.x).strength(1))
        .force("y", d3.forceY(d => d.y).strength(1))
        .force("collide", d3.forceCollide().radius(45))
        .alphaDecay(0.3);
    } else if (layout === 'force') {
      simulation = d3.forceSimulation(uniqueNodes)
        .force("link", d3.forceLink(links)
          .id(d => d.id)
          .distance(100)
          .strength(0.5))
        .force("charge", d3.forceManyBody()
          .strength(-500)
          .distanceMax(300))
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collide", d3.forceCollide().radius(50))
        .alphaDecay(0.1);
    } else if (layout === 'network') {
      simulation = d3.forceSimulation(uniqueNodes)
        .force("link", d3.forceLink(links)
          .id(d => d.id)
          .distance(150))
        .force("charge", d3.forceManyBody()
          .strength(d => d.type === 'parent' ? -1000 : -300))
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collide", d3.forceCollide().radius(60))
        .force("x", d3.forceX(width / 2).strength(0.1))
        .force("y", d3.forceY(height / 2).strength(0.1));
    } else {
      simulation = createDefaultSimulation();
    }

    const link = g.append("g")
      .selectAll("path")
      .data(links)
      .join("path")
      .attr("stroke", d => d.isCommon ? "#65ccb7" : "#707070")
      .attr("stroke-opacity", 0.6)
      .attr("stroke-width", 2)
      .attr("fill", "none")
      .attr("marker-end", d => d.isCommon ? "url(#arrow-common)" : "url(#arrow)");

    const node = g.append("g")
      .selectAll("g")
      .data(uniqueNodes)
      .join("g")
      .call(d3.drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended))
      .on("click", (event, d) => {
        setSelectedNode(d);
        setSelectedHashtag(isHashtagData ? data[0].label : null);
        event.stopPropagation();
      });

    node.append("circle")
      .attr("r", d => d.type === 'parent' ? 28 : 22)
      .attr("fill", d => {
        if (d.type === 'parent') return "#c92833";
        return d.isCommon ? "#65ccb7" : "#707070";
      })
      .attr("stroke", "#fff")
      .attr("stroke-width", 2);

    node.each(function(d) {
      const nodeGroup = d3.select(this);
      
      if (d.isHashtag) {
        nodeGroup.append("text")
          .attr("text-anchor", "middle")
          .attr("dy", "0.3em")
          .attr("fill", "white")
          .attr("font-size", "12px")
          .text(`#${d.label}`);
      } else {
        nodeGroup.append("clipPath")
          .attr("id", `clip-${d.id}`)
          .append("circle")
          .attr("r", d.type === 'parent' ? 23 : 18);

        const profileImage = (() => {
          switch (platform) {
            case 'twitter':
              return d.profile_image_url;
            case 'tiktok':
              return d.profile_avatar_url;
            case 'instagram':
            case 'facebook':
              return d.profile_photo_url;
            default:
              return '';
          }
        })();

        nodeGroup.append("image")
          .attr("xlink:href", profileImage)
          .attr("x", d.type === 'parent' ? -23 : -18)
          .attr("y", d.type === 'parent' ? -23 : -18)
          .attr("width", d.type === 'parent' ? 46 : 36)
          .attr("height", d.type === 'parent' ? 46 : 36)
          .attr("clip-path", `url(#clip-${d.id})`);
      }
    });

    simulation.on("tick", () => {
      if (layout === 'tree') {
        link.attr("d", d => {
          const source = uniqueNodes.find(n => n.id === (d.source.id || d.source));
          const target = uniqueNodes.find(n => n.id === (d.target.id || d.target));
          if (!source || !target) return "";
          return `M${source.x},${source.y}L${target.x},${target.y}`;
        });
      } else if (layout === 'circular') {
        link.attr("d", d => {
          const source = uniqueNodes.find(n => n.id === (d.source.id || d.source));
          const target = uniqueNodes.find(n => n.id === (d.target.id || d.target));
          if (!source || !target) return "";
          
          const dx = target.x - source.x;
          const dy = target.y - source.y;
          const dr = Math.sqrt(dx * dx + dy * dy ) * 1.2;
          
          return `M${source.x},${source.y}A${dr},${dr} 0 0,1 ${target.x},${target.y}`;
        });
      } else {
        link.attr("d", d => {
          const source = uniqueNodes.find(n => n.id === (d.source.id || d.source));
          const target = uniqueNodes.find(n => n.id === (d.target.id || d.target));
          if (!source || !target) return "";
          
          const dx = target.x - source.x;
          const dy = target.y - source.y;
          const dr = Math.sqrt(dx * dx + dy * dy) * 1.2;
          
          return `M${source.x},${source.y}A${dr},${dr} 0 0,1 ${target.x},${target.y}`;
        });
      }

      node.attr("transform", d => `translate(${d.x},${d.y})`);
    });

    function dragstarted(event) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    svg.on("click", () => {
      setSelectedNode(null);
      setSelectedHashtag(null);
    });

    return () => {
      simulation.stop();
    };
  }, [data, layout, relationType, maxNodes, platform]);

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        className="w-full h-[600px] bg-gray-50 rounded-lg"
        viewBox="0 0 800 600"
      />
      <Legend dataType={dataType} />
      {selectedNode && !selectedNode.isHashtag && (
        <ProfileCard
          profile={selectedNode}
          onClose={() => setSelectedNode(null)}
          selectedHashtag={selectedHashtag}
        />
      )}
    </div>
  );
};

export default RelationshipGraph;